import React, { FC, useEffect, useState, useCallback } from "react";
import CommonForm from "./CommonForm";
import axios from "../../../config/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Spinner from "../atoms/Spinner";
import { service } from "../../../services/AxiosServices";
interface PermissionDetails {
  id: string;
  classification_name: string;
}

interface Option {
  name: string;
  value: string;
}

const Permission: FC = () => {
  const [formState, setFormState] = useState({
    values: {
      category: "",
      permissionName: "",
      unique_key: "",
      per_classification: "",
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });

  // State to hold permission categories fetched from the server
  const [permissions, setPermissions] = useState<PermissionDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  // Fetch permission categories from the server on component mount
  useEffect(() => {
    setLoading(true);
    const fetchCategory = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await service<{response:any}>("/permission-classifications",'get');
        setPermissions(response.data.response || []);
      } catch (err) {
        console.error("Error fetching permissions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategory();
  }, []);

  const categories: Option[] = [
    { name: "Select", value: "" },
    ...permissions
      .sort((a, b) => a.classification_name.localeCompare(b.classification_name)) 
      .map((permission) => ({
        name: permission.classification_name,
        value: permission.id,
      })),
  ];
    // Define the form fields
  const fields = [
    {
      name: "category",
      type: "select",
      label: "Category",
      options: categories,
    },
    { name: "permissionName", type: "text", label: "Permission name" },
    {
      name: "unique_key",
      type: "text",
      label: "Keyword",
    },
  ];
  // Client-side validation logic
  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.category.trim()) {
      errors.category = 'Category is required.';
    }
    if (!values.permissionName.trim()) {
      errors.permissionName = 'Permission name is required.';
    }
    return errors;
  };
  // Handle input changes and update form state
  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;

      setFormState((prev) => {
        const updatedValues = { ...prev.values, [name]: value };

        if (name === "category") {
          updatedValues.per_classification = value;
        }

        if (name === "permissionName") {
          updatedValues.unique_key = value.trim()
            ? value.replace(/\s+/g, "_").toLowerCase()
            : ""; 
        }

        return {
          ...prev,
          values: updatedValues,
          clientErrors: { ...prev.clientErrors, [name]: "" },
          serverError: null,
        };
      });
    },
    []
  );
  // Handle form submission, validate inputs and send data to the server
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validations(formState.values);
    if (Object.keys(errors).length === 0) {
      try {
        const token = localStorage.getItem("token");
        const response = await service(
          "/permissions/manage",
          'post',
          {
            name:formState.values.permissionName,
            unique_key:formState.values.unique_key,
            per_classification:formState.values.per_classification
           
          },
         
        );
        toast.success("Permission created successfully!", { autoClose: 2000 });
        navigate('/configuration');
      } catch (err: any) {
        const errorMessage = err.response?.data?.error?.unique_key.map((ele:any)=>ele) || "Something went wrong. Please try again..";
        setFormState((prev) => ({ ...prev, serverError: errorMessage }));
      }
    } else {
      setFormState((prev) => ({ ...prev, clientErrors: errors }));
    }
  };

  const clientErrorsArray = Object.entries(formState.clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );

  return (
    <>
      {loading ? (
       <Spinner/>
      ) : (
        <>
          {formState.serverError && (
            <div className="alert alert-danger">{formState.serverError}</div>
          )}
          {clientErrorsArray.length > 0 && (
            <div className="alert alert-danger">
              <ul>
                {clientErrorsArray.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <CommonForm
            fields={fields}
            values={formState.values}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
};

export default Permission;
